import { motion } from 'motion/react';
import { useState } from 'react';

const screenshots = [
  {
    id: 1,
    title: 'Dossiers sources',
    description: 'Gestion des dossiers sources pour la sélection des fichiers MP3.',
    image: '/screenshots/dossiers_sources.jpg',
    category: 'Interface'
  },
  {
    id: 2,
    title: 'Filtres avancés',
    description: 'Panneau de configuration des critères de sélection avec sliders interactifs.',
    image: '/screenshots/criteres_selections.jpg',
    category: 'Filtres'
  },
  {
    id: 3,
    title: 'Paramétrage de l\'application',
    description: 'Options de paramétrage avancées de l\'application.',
    image: '/screenshots/parametrage_application.jpg',
    category: 'Interface'
  },
  {
    id: 4,
    title: 'Prévisualisation avant copie',
    description: 'Modal de prévisualisation des fichiers MP3 sélectionnés avant la copie.',
    image: '/screenshots/previsualisation_avant_copie.jpg',
    category: 'Aperçu'
  }
];

export default function Screenshots() {
  const [selectedCategory, setSelectedCategory] = useState('Tous');
  const [modalImage, setModalImage] = useState<null | { src: string; title: string }>(null);
  const categories = ['Tous', 'Interface', 'Filtres', 'Aperçu', 'Progression'];

  const filteredScreenshots = selectedCategory === 'Tous' 
    ? screenshots 
    : screenshots.filter(screenshot => screenshot.category === selectedCategory);

  return (
    <section id="screenshots" className="py-10">
      {/* Modale d'affichage de la capture */}
      {modalImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={() => setModalImage(null)}>
          <div className="relative bg-white dark:bg-gray-900 rounded-xl p-4 shadow-2xl flex flex-col items-center max-w-3xl w-full" onClick={e => e.stopPropagation()}>
            <img src={modalImage.src} alt={modalImage.title} className="max-h-[70vh] w-auto rounded-lg mb-4" />
            <span className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{modalImage.title}</span>
            <button
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 dark:hover:text-white text-2xl font-bold"
              onClick={() => setModalImage(null)}
              aria-label="Fermer"
            >
              ×
            </button>
          </div>
        </div>
      )}
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="text-primary-300">Captures d'écran</span>
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto mb-12">
            Découvrez l'interface moderne et intuitive de MP3 Random Selector
          </p>
          
          {/* Filtres de catégories */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                  selectedCategory === category
                    ? 'bg-gradient-to-r from-primary-500 to-accent-500 text-white shadow-lg'
                    : 'glass-effect text-gray-700 dark:text-gray-300 hover:bg-white/20 dark:hover:bg-gray-800/40'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </motion.div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {filteredScreenshots.map((screenshot, index) => (
            <motion.div
              key={screenshot.id}
              initial={{ y: 50, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="card group cursor-pointer"
            >
              {/* Affichage de l'image réelle */}
              <div
                className="w-full h-64 bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-800 rounded-lg mb-6 flex items-center justify-center group-hover:scale-105 transition-transform duration-300 overflow-hidden"
                onClick={() => setModalImage({ src: screenshot.image, title: screenshot.title })}
                style={{ cursor: 'zoom-in' }}
              >
                <img
                  src={screenshot.image}
                  alt={screenshot.title}
                  className="object-contain w-full h-full"
                  loading="lazy"
                />
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {screenshot.title}
                  </h3>
                  <span className="px-3 py-1 text-xs font-medium bg-gradient-to-r from-primary-500 to-accent-500 text-white rounded-full">
                    {screenshot.category}
                  </span>
                </div>
                <p className="text-gray-600 dark:text-gray-400">
                  {screenshot.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
        
        {filteredScreenshots.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <p className="text-gray-500 dark:text-gray-400 text-lg">
              Aucune capture d'écran disponible pour cette catégorie.
            </p>
          </motion.div>
        )}
      </div>
    </section>
  );
}
